import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { t, errorText } from '../i18n/index.ts';
import { api } from '../lib/api.ts';
import { useSession, type Me } from '../lib/session.tsx';
import { currentSubscription, enablePush, pushSupport } from '../lib/push.ts';
import { Icon, useToast } from './ui.tsx';

/**
 * Says truthfully how the player will hear about their next turn (based on their settings and
 * whether any device has device notifications on) and lets them change it right there.
 */
export function TurnNotifyControl({ lead, readOnly, turnSeconds }: { lead?: string; readOnly?: boolean; turnSeconds?: number }) {
  const { me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const devices = useQuery({ queryKey: ['push-devices'], queryFn: () => api.get<{ devices: number }>('/api/me/push') });
  const [thisDevice, setThisDevice] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void currentSubscription().then((s) => setThisDevice(!!s));
  }, []);
  if (!me) return null;
  const prefs = (me.notifyPrefs as Record<string, { push: boolean; email: boolean }>).turn ?? { push: true, email: true };
  const deviceCount = devices.data?.devices ?? 0;
  const pushWorks = prefs.push && deviceCount > 0;

  const short = turnSeconds !== undefined && turnSeconds < 30 * 60;
  const sentence = short
    ? t('turnNotify.shortTurn', { minutes: Math.round(turnSeconds! / 60) })
    : pushWorks && prefs.email
    ? t('turnNotify.both')
    : pushWorks
      ? t('turnNotify.push')
      : prefs.email
        ? prefs.push
          ? t('turnNotify.emailNoDevice')
          : t('turnNotify.email')
        : prefs.push
          ? t('turnNotify.noDevice')
          : t('turnNotify.none');

  const toggle = async (channel: 'push' | 'email') => {
    const next = !prefs[channel];
    // Optimistic: flip immediately, then save.
    qc.setQueryData(['me'], (old: Me | null | undefined) =>
      old ? { ...old, notifyPrefs: { ...old.notifyPrefs, turn: { ...prefs, [channel]: next } } } : old,
    );
    try {
      await api.patch('/api/me', { notifyPrefs: { turn: { [channel]: next } } });
    } catch (err) {
      toast(errorText(err), 'error');
    } finally {
      void qc.invalidateQueries({ queryKey: ['me'] });
    }
  };

  const enableHere = async () => {
    setBusy(true);
    try {
      const r = await enablePush();
      if (r === 'enabled') {
        setThisDevice(true);
        toast(t('push.enabled'));
      } else toast(r === 'denied' ? t('push.denied') : t('push.unsupported'), 'error');
    } catch (err) {
      toast(`${t('push.failed')} ${errorText(err)}`, 'error');
    } finally {
      setBusy(false);
      void qc.invalidateQueries({ queryKey: ['push-devices'] });
    }
  };

  if (readOnly) {
    return (
      <div className="turn-notify">
        <p className="turn-notify-text" aria-live="polite">
          {lead ? `${lead} ` : ''}
          {sentence}{' '}
          {!short && <Link to="/profiili">{t('turnNotify.change')}</Link>}
        </p>
      </div>
    );
  }

  const canEnableHere = prefs.push && pushSupport() === 'supported' && thisDevice === false && Notification.permission !== 'denied';

  return (
    <div className="turn-notify">
      <p className="turn-notify-text" aria-live="polite">
        {lead ? `${lead} ` : ''}
        {sentence}
      </p>
      <div className="turn-notify-controls" role="group" aria-label={t('turnNotify.label')}>
        <button type="button" className="notify-chip" aria-pressed={prefs.push} onClick={() => void toggle('push')}>
          <Icon name="bell" />
          {t('push.device')}
        </button>
        <button type="button" className="notify-chip" aria-pressed={prefs.email} onClick={() => void toggle('email')}>
          <Icon name="mail" />
          {t('push.email')}
        </button>
        {canEnableHere && (
          <button type="button" className="btn btn-small btn-primary" disabled={busy} onClick={() => void enableHere()}>
            {t('turnNotify.enableHere')}
          </button>
        )}
      </div>
    </div>
  );
}
